import {Component, OnInit, HostListener, DestroyRef, inject, Input} from '@angular/core';
import {FormBuilder} from '@angular/forms';
import {DatePipe} from '@angular/common';
import {combineLatestWith, Observable} from 'rxjs';
import {map, filter, startWith, take, tap, withLatestFrom} from 'rxjs/operators';
import * as R from 'ramda';
import * as RA from 'ramda-adjunct';
import {SharedFacadeService} from '@emm/shared/services/shared-facade.service';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {ItemAttribute} from '@emm/core/models/item-attribute.model';
import {LocationSet} from '@emm/core/models/location-set.model';
import {AuthFacadeService} from '@emm/auth/services/auth-facade.service';
import {LocationSetsFacadeService} from '@emm/management/services/location-sets-facade.service';
import {NotificationService} from '@emm/shared/services/notification.service';
import {FlagKeys, LaunchDarklyService} from '@emm/core/services/launch-darkly.service';

@Component({
    selector: 'emm-location-sets-form',
    styleUrls: ['./location-sets-form.component.scss'],
    templateUrl: './location-sets-form.component.html',
    standalone: false
})
export class LocationSetsFormComponent implements OnInit {

    locationSetDetail$ = this.locationSetsFacade.locationSetDetail$;
    isReadOnly$ = this.authFacade.isReadOnly$;
    @Input() isSuperAdmin: boolean;
    @Input() isAdmin: boolean;
    canEdit: boolean;
    countries = ['US', 'CA', 'PR'];
    disableLocationSetsToggle$ = this.launchDarklyService.getBooleanFlag(FlagKeys.DISABLE_LOCATION_SETS);

    disabled$ = this.isReadOnly$.pipe(combineLatestWith(this.locationSetsFacade.anyLoading$),
        map((sources: boolean[]) => R.any(R.equals(true), sources))
    );

    pipe = new DatePipe('en-US');
    private readonly destroyRef: DestroyRef = inject(DestroyRef);

    form;
    model = {};

    attributes$ = this.locationSetDetail$.pipe(
        filter(RA.isNotNil),
        tap(),
        map((item: any) => item.attributes),
        startWith([] as ItemAttribute[])
    );

    model$ = this.locationSetDetail$.pipe(
        withLatestFrom(this.disableLocationSetsToggle$),
        filter(RA.isNotNil),
        map(([locationSet, toggle]) => {
            if (locationSet?.scope === 'Custom' && toggle) {
                this.form = this.formBuilder.group({
                    name: [locationSet.name, {disabled: !this.canEdit, nonNullable: true}],
                    description: [locationSet.description, {disabled: !this.canEdit, nonNullable: true}],
                    countryCode: [locationSet.countryCode, {disabled: !this.canEdit, nonNullable: true}],
                });
            } else {
                this.form = this.formBuilder.group({
                    name: [locationSet?.name, {disabled: !this.canEdit, nonNullable: true}],
                    description: [locationSet?.description, {disabled: !this.canEdit, nonNullable: true}],
                    countryCode: [locationSet?.countryCode, {disabled: !this.canEdit, nonNullable: true}],
                });
            }
        }),
    );

    constructor(
        private authFacade: AuthFacadeService,
        private locationSetsFacade: LocationSetsFacadeService,
        private notificationService: NotificationService,
        private sharedFacade: SharedFacadeService,
        private formBuilder: FormBuilder,
        private launchDarklyService: LaunchDarklyService
    ) {
    }


    ngOnInit(): void {
        this.canEdit = this.isSuperAdmin || this.isAdmin;
        this.model$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
            if (!this.canEdit) {
                this.form.disable();
            }
        });
    }


    cancel(): void {
        this.locationSetDetail$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
            (locationSet: LocationSet) => this.model = locationSet);
        this.form.reset(this.model);
    }


    submit(locationSetTag: string): void {
        const formData = {
            countryCode: this.form.controls.countryCode.value,
            description: this.form.controls.description.value,
            name: this.form.controls.name.value,
            tag: locationSetTag,
        };
        this.form.markAsPristine();

        this.locationSetsFacade
            .updateLocationSet(locationSetTag, formData)
            .pipe(
                takeUntilDestroyed(this.destroyRef)
            )
            .subscribe(updatedLocationSet => {
                if (updatedLocationSet) {
                    this.locationSetsFacade.reloadLocationSets(updatedLocationSet.scope.toUpperCase());
                    this.notificationService.success(`Saved : ${formData['name']}`);
                    return;
                }
                this.notificationService.error(`Could not save : ${formData['name']}`);
            });
    }


    hasChanges(): boolean {
        return this.form && this.form.dirty;
    }


    isFormReady(): boolean {
        return this.form && this.form.dirty && !this.form.invalid;
    }
}
